import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Plus, Trash2, Users, X, Eye, EyeOff, ChevronLeft, ChevronRight, Key } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Gérer les cas où REACT_APP_BACKEND_URL contient déjà /api
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 10;

const GestionComptes = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);

  // Password management state
  const [passwordAction, setPasswordAction] = useState(''); // 'view' or 'change'
  const [rhPassword, setRhPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showRhPassword, setShowRhPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [rhPasswordVerified, setRhPasswordVerified] = useState(false);
  const [currentUserPassword, setCurrentUserPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Create user form state
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
    role: '',
    company: ''
  });
  const [showCreatePassword, setShowCreatePassword] = useState(false);

  // Manage user state (new workflow)
  const [manageUserForm, setManageUserForm] = useState({
    searchUsername: '',
    username: '',
    company: '',
    company_id: '',
    position: '',
    role: '',
    photo: '',
    email: ''
  });
  const [prysmaUsers, setPrysmaUsers] = useState([]);
  const [filteredPrysmaUsers, setFilteredPrysmaUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingPrysmaUsers, setLoadingPrysmaUsers] = useState(false);
  const [userFound, setUserFound] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Calculate pagination when users data changes
    const total = Math.ceil(users.length / ITEMS_PER_PAGE);
    setTotalPages(total);
    
    // Reset to page 1 if current page exceeds total pages
    if (currentPage > total && total > 0) {
      setCurrentPage(1);
    }
  }, [users, currentPage]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const usersData = await response.json();
        setUsers(usersData);
      } else {
        toast.error('Erreur lors du chargement des comptes');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Get paginated users
  const getPaginatedUsers = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return users.slice(startIndex, endIndex);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToRH = () => {
    navigate('/rh');
  };

  // Load all Prysma users when modal opens
  const loadPrysmaUsers = async () => {
    setLoadingPrysmaUsers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/list-prysma`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const usersData = await response.json();
        setPrysmaUsers(usersData);
        setFilteredPrysmaUsers(usersData);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du chargement des utilisateurs');
      }
    } catch (error) {
      console.error('Error loading Prysma users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoadingPrysmaUsers(false);
    }
  };

  // Filter users based on search query
  const handleSearchQueryChange = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredPrysmaUsers(prysmaUsers);
    } else {
      const lowercaseQuery = query.toLowerCase();
      const filtered = prysmaUsers.filter(user => 
        user.first_name?.toLowerCase().includes(lowercaseQuery) ||
        user.last_name?.toLowerCase().includes(lowercaseQuery) ||
        user.username?.toLowerCase().includes(lowercaseQuery) ||
        user.position?.toLowerCase().includes(lowercaseQuery) ||
        user.company?.toLowerCase().includes(lowercaseQuery)
      );
      setFilteredPrysmaUsers(filtered);
    }
  };

  // Select a user from the list
  const handleSelectUser = (user) => {
    setManageUserForm({
      searchUsername: user.username,
      username: user.username,
      company: user.company,
      company_id: user.company_id,
      position: user.position,
      photo: user.photo,
      email: user.email,
      role: ''
    });
    setUserFound(true);
  };

  // New manage user functions (keeping old search function for compatibility)
  const handleSearchUser = async () => {
    if (!manageUserForm.searchUsername.trim()) {
      toast.error('Veuillez entrer un ID utilisateur');
      return;
    }

    setSearchingUser(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/search-prysma?username=${encodeURIComponent(manageUserForm.searchUsername)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setManageUserForm({
          ...manageUserForm,
          username: userData.username,
          company: userData.company,
          company_id: userData.company_id,
          position: userData.position,
          role: '' // RH doit choisir le rôle
        });
        setUserFound(true);
        toast.success(`Utilisateur trouvé : ${userData.username}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Utilisateur non trouvé dans la base Prysma');
        setUserFound(false);
      }
    } catch (error) {
      console.error('Error searching user:', error);
      toast.error('Erreur lors de la recherche');
      setUserFound(false);
    } finally {
      setSearchingUser(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!manageUserForm.role) {
      toast.error('Veuillez sélectionner un rôle');
      return;
    }

    setSavingPermissions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/manage-permissions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: manageUserForm.username,
          role: manageUserForm.role,
          company: manageUserForm.company,
          company_id: manageUserForm.company_id,
          position: manageUserForm.position
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Permissions enregistrées avec succès');
        setShowCreateModal(false);
        setManageUserForm({
          searchUsername: '',
          username: '',
          company: '',
          company_id: '',
          position: '',
          role: ''
        });
        setUserFound(false);
        fetchUsers(); // Refresh the users list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleCreateUser = async () => {
    // Validation
    if (!createForm.username.trim()) {
      toast.error('L\'ID est requis');
      return;
    }
    if (!createForm.email.trim()) {
      toast.error('L\'adresse email est requise');
      return;
    }
    if (!createForm.password) {
      toast.error('Le mot de passe est requis');
      return;
    }
    if (!createForm.role) {
      toast.error('Le rôle est requis');
      return;
    }
    if (!createForm.company && createForm.role !== 'rh') {
      toast.error('La société est requise pour ce rôle');
      return;
    }

    // Validate username format (prénom.nom)
    const usernameRegex = /^[a-zA-Z]+\.[a-zA-Z]+$/;
    if (!usernameRegex.test(createForm.username)) {
      toast.error('L\'ID doit être au format prénom.nom');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(createForm.email)) {
      toast.error('L\'adresse email n\'est pas valide');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: createForm.username.trim(),
          email: createForm.email.trim(), // Utiliser l'email saisi par l'utilisateur
          password: createForm.password,
          role: createForm.role,
          company: createForm.role === 'rh' ? null : createForm.company
        }),
      });

      if (response.ok) {
        toast.success('Compte créé avec succès');
        setShowCreateModal(false);
        setCreateForm({
          username: '',
          email: '',
          password: '',
          role: '',
          company: ''
        });
        fetchUsers(); // Refresh the list
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de la création du compte');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Erreur de connexion');
    }
  };

  const handleDeleteUser = async () => {
    if (!deletePassword) {
      toast.error('Veuillez saisir votre mot de passe');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: deletePassword
        }),
      });

      if (response.ok) {
        toast.success('Compte supprimé avec succès');
        setShowDeleteModal(false);
        setSelectedUser(null);
        setDeletePassword('');
        fetchUsers(); // Refresh the list
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de la suppression du compte');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erreur de connexion');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'rh': return 'RH';
      case 'admin': return 'Admin';
      case 'manager': return 'Manager';
      default: return role;
    }
  };

  // Password management functions
  const handlePasswordClick = (userData) => {
    setSelectedUser(userData);
    setPasswordAction('');
    setRhPassword('');
    setNewPassword('');
    setRhPasswordVerified(false);
    setCurrentUserPassword('');
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedUser(null);
    setPasswordAction('');
    setRhPassword('');
    setNewPassword('');
    setRhPasswordVerified(false);
    setCurrentUserPassword('');
    setPasswordLoading(false);
  };

  const verifyRHPassword = async () => {
    if (!rhPassword) {
      toast.error('Veuillez saisir votre mot de passe RH');
      return false;
    }

    setPasswordLoading(true);
    try {
      const response = await fetch(`${API}/auth/verify-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: rhPassword
        }),
      });

      if (response.ok) {
        setRhPasswordVerified(true);
        return true;
      } else {
        toast.error('Mot de passe RH incorrect');
        return false;
      }
    } catch (error) {
      console.error('Error verifying RH password:', error);
      toast.error('Erreur de connexion');
      return false;
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordAction = async (action) => {
    if (!rhPasswordVerified) {
      const verified = await verifyRHPassword();
      if (!verified) return;
    }

    if (action === 'view') {
      await viewUserPassword();
    } else if (action === 'change') {
      // Just enable the change password form
      setRhPasswordVerified(true);
    }
  };

  const viewUserPassword = async () => {
    setPasswordLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/${selectedUser.id}/password`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Password data received:', data); // Debug log
        setCurrentUserPassword(data.password);
        toast.success('Mot de passe récupéré');
      } else {
        const errorText = await response.text();
        console.error('Password retrieval error:', response.status, errorText);
        toast.error(`Erreur lors de la récupération du mot de passe: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching user password:', error);
      toast.error('Erreur de connexion');
    } finally {
      setPasswordLoading(false);
    }
  };

  const changeUserPassword = async () => {
    if (!newPassword) {
      toast.error('Veuillez saisir le nouveau mot de passe');
      return;
    }

    setPasswordLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/${selectedUser.id}/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: newPassword,
          rh_password: rhPassword
        }),
      });

      if (response.ok) {
        toast.success('Mot de passe modifié avec succès');
        handleClosePasswordModal();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors du changement du mot de passe');
      }
    } catch (error) {
      console.error('Error changing user password:', error);
      toast.error('Erreur de connexion');
    } finally {
      setPasswordLoading(false);
    }
  };

  const companies = ['Ma Conciergerie', 'Mon Evenementiel', 'SISENS', 'Horizon Solution', 'Prysme'];
  const roles = ['admin', 'manager', 'rh'];

  return (
    <div className="gestion-comptes-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToRH}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <Users size={24} style={{ color: '#eb5d2d' }} />
        </div>
        <h1>Gestion des utilisateurs</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        <div className="gestion-comptes-container">
          {/* Create Account Button */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Liste des comptes utilisateurs</h2>
            <Button
              onClick={() => {
                setShowCreateModal(true);
                loadPrysmaUsers();
              }}
              className="btn-primary"
              style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
            >
              <Users size={16} className="mr-2" />
              Gérer un utilisateur
            </Button>
          </div>

          {/* Users Table */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <Card className="users-table-card">
                <CardContent className="p-0">
                  <div className="users-table-container">
                    <table className="users-table-compact">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Type de compte</th>
                          <th>Société</th>
                          <th>Date de création</th>
                          <th>Mot de passe</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedUsers().map((userData) => (
                          <tr key={userData.id}>
                            <td className="font-medium">{userData.username}</td>
                            <td>
                              <span className={`role-badge-compact role-${userData.role}`}>
                                {getRoleLabel(userData.role)}
                              </span>
                            </td>
                            <td>{userData.company || 'N/A'}</td>
                            <td>{formatDate(userData.created_at)}</td>
                            <td>
                              {userData.id !== user.id && (
                                <Button
                                  onClick={() => handlePasswordClick(userData)}
                                  variant="outline"
                                  size="sm"
                                  className="btn-password-compact"
                                  title="Gérer le mot de passe"
                                >
                                  <Key size={12} />
                                </Button>
                              )}
                            </td>
                            <td>
                              {userData.id !== user.id && (
                                <Button
                                  onClick={() => {
                                    setSelectedUser(userData);
                                    setShowDeleteModal(true);
                                  }}
                                  variant="destructive"
                                  size="sm"
                                  className="btn-delete-compact"
                                >
                                  <Trash2 size={12} />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {users.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        Aucun compte trouvé
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Page {currentPage} sur {totalPages} ({users.length} comptes au total)
                  </div>
                  <div className="pagination-controls">
                    <Button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="sm"
                      className="pagination-btn"
                    >
                      <ChevronLeft size={16} />
                      Précédent
                    </Button>
                    
                    <div className="pagination-pages">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <Button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                          style={currentPage === page ? { backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' } : {}}
                        >
                          {page}
                        </Button>
                      ))}
                    </div>

                    <Button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      variant="outline"
                      size="sm"
                      className="pagination-btn"
                    >
                      Suivant
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-container" style={{ maxWidth: '900px' }}>
            <Card className="modal-card">
              <CardHeader className="modal-header">
                <div className="flex justify-between items-center">
                  <CardTitle className="modal-title">
                    <Users size={20} className="mr-2" />
                    Gérer un utilisateur
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowCreateModal(false);
                      setManageUserForm({
                        searchUsername: '',
                        username: '',
                        company: '',
                        company_id: '',
                        position: '',
                        photo: '',
                        email: '',
                        role: ''
                      });
                      setUserFound(false);
                      setSearchQuery('');
                      setPrysmaUsers([]);
                      setFilteredPrysmaUsers([]);
                    }}
                    className="modal-close"
                  >
                    <X size={20} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="modal-content">
                {!userFound ? (
                  <>
                    {/* Search bar */}
                    <div className="mb-4">
                      <Label htmlFor="searchQuery" className="form-label mb-2">
                        Rechercher un utilisateur
                      </Label>
                      <Input
                        id="searchQuery"
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleSearchQueryChange(e.target.value)}
                        className="form-input"
                        placeholder="Nom, prénom, poste ou société..."
                      />
                    </div>

                    {/* Users list */}
                    {loadingPrysmaUsers ? (
                      <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                      </div>
                    ) : (
                      <div 
                        className="border rounded-lg overflow-hidden"
                        style={{ 
                          maxHeight: '400px', 
                          overflowY: 'auto',
                          backgroundColor: '#f9fafb'
                        }}
                      >
                        {filteredPrysmaUsers.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            {prysmaUsers.length === 0 ? 'Chargement des utilisateurs...' : 'Aucun utilisateur trouvé'}
                          </div>
                        ) : (
                          <div className="divide-y">
                            {filteredPrysmaUsers.map((user) => (
                              <div
                                key={user.username}
                                onClick={() => handleSelectUser(user)}
                                className="p-4 hover:bg-white cursor-pointer transition-colors flex items-center gap-4"
                                style={{ borderBottom: '1px solid #e5e7eb' }}
                              >
                                {/* Photo */}
                                <div className="flex-shrink-0">
                                  {user.photo ? (
                                    <img
                                      src={user.photo}
                                      alt={`${user.first_name} ${user.last_name}`}
                                      className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div 
                                    className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold text-lg border-2 border-orange-200"
                                    style={{ display: user.photo ? 'none' : 'flex' }}
                                  >
                                    {user.first_name?.[0]}{user.last_name?.[0]}
                                  </div>
                                </div>

                                {/* User info */}
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-gray-900">
                                    {user.first_name} {user.last_name}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {user.username}
                                  </div>
                                  <div className="text-sm text-gray-500 mt-1">
                                    {user.position} • {user.company}
                                  </div>
                                </div>

                                {/* Arrow icon */}
                                <div className="flex-shrink-0 text-gray-400">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="form-container">
                    {/* Selected user info with photo */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg flex items-center gap-4">
                      <div className="flex-shrink-0">
                        {manageUserForm.photo ? (
                          <img
                            src={manageUserForm.photo}
                            alt="User"
                            className="w-16 h-16 rounded-full object-cover border-2 border-orange-200"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold text-xl border-2 border-orange-200"
                          style={{ display: manageUserForm.photo ? 'none' : 'flex' }}
                        >
                          {manageUserForm.username?.split('.')[0]?.[0]?.toUpperCase()}
                          {manageUserForm.username?.split('.')[1]?.[0]?.toUpperCase()}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-lg text-gray-900">
                          {manageUserForm.username}
                        </div>
                        <div className="text-sm text-gray-600">
                          {manageUserForm.email}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setUserFound(false);
                          setManageUserForm({
                            searchUsername: '',
                            username: '',
                            company: '',
                            company_id: '',
                            position: '',
                            photo: '',
                            email: '',
                            role: ''
                          });
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        Changer
                      </Button>
                    </div>

                    {/* Company */}
                    <div className="form-group">
                      <Label htmlFor="company" className="form-label">
                        Société
                      </Label>
                      <Input
                        id="company"
                        type="text"
                        value={manageUserForm.company}
                        className="form-input bg-gray-100"
                        disabled
                      />
                    </div>

                    {/* Position */}
                    <div className="form-group">
                      <Label htmlFor="position" className="form-label">
                        Intitulé du poste
                      </Label>
                      <Input
                        id="position"
                        type="text"
                        value={manageUserForm.position}
                        className="form-input bg-gray-100"
                        disabled
                      />
                    </div>

                    {/* Role Selection */}
                    <div className="form-group">
                      <Label htmlFor="role" className="form-label">
                        Rôle *
                      </Label>
                      <select 
                        id="role"
                        value={manageUserForm.role} 
                        onChange={(e) => setManageUserForm(prev => ({ ...prev, role: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="">Sélectionnez un rôle</option>
                        <option value="manager">Manager</option>
                        <option value="rh">RH</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>

                    <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md">
                      ℹ️ Les permissions seront configurées automatiquement en fonction de la société ({manageUserForm.company})
                    </div>
                  </div>
                )}

                {/* Modal Actions */}
                <div className="modal-actions mt-6">
                  {userFound && (
                    <Button
                      onClick={handleSavePermissions}
                      disabled={savingPermissions}
                      className="btn-primary"
                      style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
                    >
                      {savingPermissions ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setShowCreateModal(false);
                      setManageUserForm({
                        searchUsername: '',
                        username: '',
                        company: '',
                        company_id: '',
                        position: '',
                        photo: '',
                        email: '',
                        role: ''
                      });
                      setUserFound(false);
                      setSearchQuery('');
                      setPrysmaUsers([]);
                      setFilteredPrysmaUsers([]);
                    }}
                    variant="outline"
                    className="btn-cancel"
                  >
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-container">
            <Card className="modal-card">
              <CardHeader className="modal-header">
                <div className="flex justify-between items-center">
                  <CardTitle className="modal-title text-red-600">
                    <Trash2 size={20} className="mr-2" />
                    Supprimer le compte
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedUser(null);
                      setDeletePassword('');
                    }}
                    className="modal-close"
                  >
                    <X size={20} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="modal-content">
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-medium mb-2">
                    Attention ! Cette action est irréversible.
                  </p>
                  <p className="text-red-700 text-sm">
                    Vous êtes sur le point de supprimer définitivement le compte :
                  </p>
                  <p className="text-red-800 font-medium mt-1">
                    {selectedUser.username} ({getRoleLabel(selectedUser.role)})
                  </p>
                </div>

                <div className="form-group">
                  <Label htmlFor="deletePassword" className="form-label">
                    Confirmez avec votre mot de passe RH *
                  </Label>
                  <div className="relative">
                    <Input
                      id="deletePassword"
                      type={showDeletePassword ? "text" : "password"}
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      className="form-input pr-10"
                      placeholder="Votre mot de passe RH"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowDeletePassword(!showDeletePassword)}
                    >
                      {showDeletePassword ? (
                        <EyeOff size={16} className="text-gray-400" />
                      ) : (
                        <Eye size={16} className="text-gray-400" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="modal-actions">
                  <Button
                    onClick={handleDeleteUser}
                    className="btn-danger"
                    style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }}
                  >
                    <Trash2 size={16} className="mr-2" />
                    Supprimer définitivement
                  </Button>
                  <Button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedUser(null);
                      setDeletePassword('');
                    }}
                    variant="outline"
                    className="btn-cancel"
                  >
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Password Management Modal */}
      {showPasswordModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-container">
            <Card className="modal-card">
              <CardHeader className="modal-header">
                <div className="flex justify-between items-center">
                  <CardTitle className="modal-title" style={{ color: '#eb5d2d' }}>
                    <Key size={20} className="mr-2" />
                    Gestion du mot de passe - {selectedUser.username}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClosePasswordModal}
                    className="modal-close"
                  >
                    <X size={20} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="modal-content">
                {!rhPasswordVerified ? (
                  // Step 1: RH Password Verification
                  <div className="password-verification-step">
                    <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-orange-800 font-medium mb-2">
                        Vérification de sécurité requise
                      </p>
                      <p className="text-orange-700 text-sm">
                        Pour accéder à la gestion des mots de passe, veuillez confirmer votre identité RH.
                      </p>
                    </div>

                    <div className="form-group">
                      <Label htmlFor="rhPassword" className="form-label">
                        Votre mot de passe RH *
                      </Label>
                      <div className="relative">
                        <Input
                          id="rhPassword"
                          type={showRhPassword ? "text" : "password"}
                          value={rhPassword}
                          onChange={(e) => setRhPassword(e.target.value)}
                          className="form-input pr-10"
                          placeholder="Saisissez votre mot de passe RH"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowRhPassword(!showRhPassword)}
                        >
                          {showRhPassword ? (
                            <EyeOff size={16} className="text-gray-400" />
                          ) : (
                            <Eye size={16} className="text-gray-400" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="password-action-buttons">
                      <Button
                        onClick={() => {
                          setPasswordAction('view');
                          handlePasswordAction('view');
                        }}
                        disabled={passwordLoading || !rhPassword}
                        className="btn-view-password"
                        style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
                      >
                        <Eye size={16} className="mr-2" />
                        Visualiser le mot de passe
                      </Button>
                      <Button
                        onClick={() => {
                          setPasswordAction('change');
                          handlePasswordAction('change');
                        }}
                        disabled={passwordLoading || !rhPassword}
                        className="btn-change-password"
                        style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}
                      >
                        <Key size={16} className="mr-2" />
                        Changer le mot de passe
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Step 2: Password Management Actions
                  <div className="password-management-step">
                    {passwordAction === 'view' && (
                      <div className="password-view-section">
                        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-blue-800 font-medium mb-2">
                            Mot de passe actuel du compte
                          </p>
                          <p className="text-blue-700 text-sm mb-3">
                            Compte : <strong>{selectedUser.username}</strong> ({getRoleLabel(selectedUser.role)})
                          </p>
                          
                          {currentUserPassword ? (
                            <div className="password-display">
                              <Label className="form-label">Mot de passe :</Label>
                              <div className="password-display-box">
                                <code className="password-text">{currentUserPassword}</code>
                              </div>
                            </div>
                          ) : passwordLoading ? (
                            <p className="text-blue-600">Récupération du mot de passe en cours...</p>
                          ) : (
                            <p className="text-gray-600">Cliquez sur "Visualiser le mot de passe" pour commencer</p>
                          )}
                        </div>
                      </div>
                    )}

                    {passwordAction === 'change' && (
                      <div className="password-change-section">
                        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-yellow-800 font-medium mb-2">
                            Changement de mot de passe
                          </p>
                          <p className="text-yellow-700 text-sm">
                            Compte : <strong>{selectedUser.username}</strong> ({getRoleLabel(selectedUser.role)})
                          </p>
                        </div>

                        <div className="form-group">
                          <Label htmlFor="newPassword" className="form-label">
                            Nouveau mot de passe *
                          </Label>
                          <div className="relative">
                            <Input
                              id="newPassword"
                              type={showNewPassword ? "text" : "password"}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="form-input pr-10"
                              placeholder="Saisissez le nouveau mot de passe"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                            >
                              {showNewPassword ? (
                                <EyeOff size={16} className="text-gray-400" />
                              ) : (
                                <Eye size={16} className="text-gray-400" />
                              )}
                            </Button>
                          </div>
                        </div>

                        <div className="password-change-actions">
                          <Button
                            onClick={changeUserPassword}
                            disabled={passwordLoading || !newPassword}
                            className="btn-primary"
                            style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}
                          >
                            <Key size={16} className="mr-2" />
                            Valider le changement
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Modal Actions */}
                <div className="modal-actions">
                  <Button
                    onClick={handleClosePasswordModal}
                    variant="outline"
                    className="btn-cancel"
                  >
                    {rhPasswordVerified && passwordAction ? 'Fermer' : 'Annuler'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionComptes;